import React, { useState } from 'react';
import { HelpCircle, Timer, Target, Heart, X } from 'lucide-react';
import type { Todo } from '../types';
import { commentsAPI } from '../services/api';

interface TaskCompletionHelperProps {
  todo: Todo;
  onStartPomodoro?: (todo: Todo) => void;
}

interface Strategy {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  action: () => void;
}

export const TaskCompletionHelper: React.FC<TaskCompletionHelperProps> = ({ 
  todo, 
  onStartPomodoro 
}) => {
  const [showStrategies, setShowStrategies] = useState(false);
  const [nextStep, setNextStep] = useState('');
  const [motivation, setMotivation] = useState('');
  const [showNextStepInput, setShowNextStepInput] = useState(false);
  const [showMotivationInput, setShowMotivationInput] = useState(false);


  const handleStartPomodoro = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onStartPomodoro) {
      onStartPomodoro(todo);
      // Note: The pomodoro timer will use user's default settings
      // TODO: Enhance to force 25 minutes specifically
    } else {
      // Fallback - just show an alert for now
      alert('Pomodoro timer would start for 25 minutes. Focus on this task!');
    }
    setShowStrategies(false);
  };

  const handleNextStepBreakdown = () => {
    setShowNextStepInput(true);
    setShowStrategies(false);
  };

  const handleMotivationReminder = () => {
    setShowMotivationInput(true);
    setShowStrategies(false);
  };

  const handleSaveNextStep = async () => {
    if (nextStep.trim() && todo.id) {
      try {
        // Save the next step as a comment
        const commentData = {
          content: `🎯 **Next step:** ${nextStep.trim()}`,
        };

        await commentsAPI.create(todo.id, commentData);
        alert(`Next step added: "${nextStep}"\n\nNow focus on just this one small step!`);
        setNextStep('');
        setShowNextStepInput(false);
      } catch (error) {
        console.error('Failed to save next step:', error);
        alert('Failed to add next step. Please try again.');
      }
    } else {
      setShowNextStepInput(false);
    }
  };

  const handleSaveMotivation = async () => {
    if (motivation.trim() && todo.id) {
      try {
        // Create a comment with the motivation
        const commentData = {
          content: `💪 **Why this matters:** ${motivation.trim()}`,
        };
        
        await commentsAPI.create(todo.id, commentData);
        alert(`Motivation reminder saved!\n\n"${motivation}"\n\nRemember why this matters to you!`);
        setMotivation('');
        setShowMotivationInput(false);
        
        // Note: The TodoForm will need to refresh to show the new comment
        // This could be enhanced with a callback to refresh the parent
      } catch (error) {
        console.error('Failed to create motivation comment:', error);
        alert('Failed to save motivation. Please try again.');
      }
    } else {
      setShowMotivationInput(false);
    }
  };

  const strategies: Strategy[] = [
    {
      id: 'pomodoro',
      title: 'Start 25-min Pomodoro',
      icon: <Timer className="w-4 h-4" />,
      description: 'Focus for 25 minutes with a timer',
      action: handleStartPomodoro
    },
    {
      id: 'next-step',
      title: 'Break into next step',
      icon: <Target className="w-4 h-4" />,
      description: 'Write down the smallest possible next action',
      action: handleNextStepBreakdown
    },
    {
      id: 'motivation',
      title: 'Remember why it matters',
      icon: <Heart className="w-4 h-4" />,
      description: 'Connect with your goals and values',
      action: handleMotivationReminder
    }
  ];

  return (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setShowStrategies(true);
        }}
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-md transition-colors"
        title="Get help completing this task"
      >
        <HelpCircle className="w-4 h-4" />
        <span>Need help completing this?</span>
      </button>

      {/* Strategies Modal */}
      {showStrategies && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
          onClick={(e) => {
            e.stopPropagation();
            setShowStrategies(false);
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Help Complete: {todo.title}
              </h3>
              <button
                onClick={() => setShowStrategies(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-3">
              {strategies.map((strategy) => (
                <button
                  key={strategy.id}
                  onClick={strategy.action}
                  className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="text-purple-600">
                      {strategy.icon}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{strategy.title}</div>
                      <div className="text-sm text-gray-600">{strategy.description}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Next Step Input Modal */}
      {showNextStepInput && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
          onClick={(e) => {
            e.stopPropagation();
            setShowNextStepInput(false);
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                What's the smallest next step?
              </h3>
              <button
                onClick={() => setShowNextStepInput(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Think of the tiniest action you can take right now to move this task forward.
              </p>
              <textarea
                value={nextStep}
                onChange={(e) => setNextStep(e.target.value)}
                placeholder="e.g., 'Open the document' or 'Write one sentence' or 'Find the phone number'"
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                rows={3}
                autoFocus
              />
              <div className="flex space-x-3">
                <button
                  onClick={handleSaveNextStep}
                  className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors"
                >
                  Focus on this step
                </button>
                <button
                  onClick={() => setShowNextStepInput(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Motivation Input Modal */}
      {showMotivationInput && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
          onClick={(e) => {
            e.stopPropagation();
            setShowMotivationInput(false);
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Why is this important?
              </h3>
              <button
                onClick={() => setShowMotivationInput(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                How does completing this task align with your goals and values? What will it help you achieve?
              </p>
              <textarea
                value={motivation}
                onChange={(e) => setMotivation(e.target.value)}
                placeholder="e.g., 'This helps me build better relationships' or 'This advances my career goals' or 'This makes me feel more organized'"
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                rows={4}
                autoFocus
              />
              <div className="flex space-x-3">
                <button
                  onClick={handleSaveMotivation}
                  className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors"
                >
                  Remember this motivation
                </button>
                <button
                  onClick={() => setShowMotivationInput(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};